// ABOUTME: The Star/Triangle/Storm tally button — a square icon-button under the nalu-v1 theme
// ABOUTME: (matching the Keiki Dashboard mockup), the existing outlined MUI Button otherwise.

import React from 'react';
import { Button } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import naluV1Theme from '../theme/theme.nalu-v1';
import { getBehaviorLabel, parseBehaviorType } from '../utils/behaviorUtils';
import { StarConstellationIcon } from './StarConstellationIcon';
import { TriangleCloudIcon } from './TriangleCloudIcon';

interface BehaviorIconButtonProps {
  behaviorType: 'star' | 'triangle' | 'square';
  icon: React.ElementType;
  onClick: () => void;
  /** Non-nalu sizing only — nalu-v1 always renders a fixed 72px square across every surface. */
  size?: 'default' | 'compact';
}

const PALETTE_KEY: Record<BehaviorIconButtonProps['behaviorType'], 'success' | 'warning' | 'error'> = {
  star: 'success',
  triangle: 'warning',
  square: 'error',
};

const BehaviorIconButton: React.FC<BehaviorIconButtonProps> = ({ behaviorType, icon: Icon, onClick, size = 'default' }) => {
  const theme = useTheme();
  const isNaluV1 = theme.logoSrc === naluV1Theme.logoSrc;
  const paletteKey = PALETTE_KEY[behaviorType];
  const ariaLabel = getBehaviorLabel(parseBehaviorType(behaviorType));

  if (isNaluV1) {
    return (
      <Button
        aria-label={ariaLabel}
        onClick={onClick}
        sx={{
          minWidth: { xs: 60, sm: 72 },
          minHeight: { xs: 60, sm: 72 },
          width: { xs: 60, sm: 72 },
          height: { xs: 60, sm: 72 },
          borderRadius: '18px',
          bgcolor: theme.palette.background.paper,
          boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.12)}`,
          p: 0,
          minPadding: 0,
          '&:hover': {
            bgcolor: theme.palette.background.paper,
            transform: 'translateY(-2px)',
            boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.18)}`,
          },
          '&:active': {
            transform: 'translateY(0px) scale(0.97)',
            boxShadow: `0 2px 6px ${alpha(theme.palette.common.black, 0.2)}`,
          },
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {behaviorType === 'star' && <StarConstellationIcon size={32} decorative />}
        {behaviorType === 'triangle' && <TriangleCloudIcon size={32} decorative />}
        {behaviorType === 'square' && <Icon sx={{ fontSize: { xs: 28, sm: 32 } }} />}
      </Button>
    );
  }

  // "compact" (AllKidsQuickRecordTab) and "default" (DashboardRedesigned) preserve each
  // call site's pre-existing sx byte-for-byte — they diverged independently before this
  // component existed, so unifying them here would be a visual change, not a refactor.
  if (size === 'compact') {
    return (
      <Button
        variant="outlined"
        aria-label={ariaLabel}
        onClick={onClick}
        sx={{
          bgcolor: alpha(theme.palette[paletteKey].main, 0.1),
          color: theme.palette[paletteKey].main,
          borderColor: `${paletteKey}.main`,
          borderWidth: 2,
          minWidth: { xs: 60, sm: 70 },
          minHeight: { xs: 50, sm: 58 },
          borderRadius: 3,
          '&:hover': { borderWidth: 3 },
        }}
      >
        <Icon sx={{ fontSize: { xs: 26, sm: 30 } }} />
      </Button>
    );
  }

  return (
    <Button
      variant="outlined"
      aria-label={ariaLabel}
      onClick={onClick}
      sx={{
        bgcolor: alpha(theme.palette[paletteKey].main, 0.1),
        color: theme.palette[paletteKey].main,
        borderColor: `${paletteKey}.main`,
        borderWidth: 2,
        '&:hover': {
          bgcolor: alpha(theme.palette[paletteKey].main, 0.2),
          borderWidth: 3,
          transform: 'translateY(-2px)',
          boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.15)}`,
        },
        minWidth: { xs: 70, sm: 80 },
        minHeight: { xs: 55, sm: 65 },
        px: { xs: 1.5, sm: 2 },
        py: { xs: 1, sm: 1.5 },
        borderRadius: 3,
        '&:active': {
          transform: 'translateY(0px) scale(0.97)',
          boxShadow: `0 2px 6px ${alpha(theme.palette.common.black, 0.2)}`,
        },
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <Icon sx={{ fontSize: { xs: 28, sm: 32 } }} />
    </Button>
  );
};

export default BehaviorIconButton;
